import React, { useCallback, useMemo } from "react";
import {
  alpha,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DeviceCardPanel from "../deviceCardPanel";
import DeviceTypeAvatar from "../DeviceTypeAvatar";
import { BatteryFull, AccessTime } from "@mui/icons-material";
import { DEVICE_TYPE_LAYOUT } from "../../constants/deviceColumns";
import DeviceStatusIcon from "../DeviceStatusIcon";
import {
  formatDate,
  formatTemperature,
  formatDecimal,
} from "../../utils/formatters";
import {
  convertMvToPercent,
  convertPercentToColour,
} from "../../utils/dataTransforms";
import { useTranslation } from "react-i18next";
import {
  getBatteryMillivolts,
  getDeviceLastData,
  getDeviceFreshnessTime,
  isDeviceActive,
} from "../../utils/deviceUtils";

const getFormattedContent = (field, value) => {
  if (value === undefined || value === null) return "N/A";

  if (
    field.includes("temperature") ||
    field === "temp" ||
    field.includes("temp_")
  ) {
    return formatTemperature(value, true);
  } else if (
    field.includes("humidity") ||
    field === "RH" ||
    field === "rh" ||
    field === "soil_moisture" ||
    field === "leaf_moisture" ||
    field === "moisture"
  ) {
    return formatDecimal(value) + "%";
  } else if (field === "co2" || field === "CO2") {
    return Array.isArray(value)
      ? formatDecimal(value[0]) + "ppm"
      : formatDecimal(value) + "ppm";
  } else if (field === "distance") {
    return formatDecimal(value) + "mm";
  } else if (field === "tvoc" || field === "TVOC") {
    return Array.isArray(value)
      ? formatDecimal(value[0]) + "ppb"
      : formatDecimal(value) + "ppb";
  } else if (field.includes("current")) {
    return formatDecimal(value) + "A";
  } else if (field.includes("voltage")) {
    return formatDecimal(value) + "V";
  } else if (field.includes("watt") || field === "watts") {
    return formatDecimal(value) + "W";
  } else if (field === "energy") {
    return formatDecimal(value) + "kWh";
  } else if (field === "time") {
    return formatDecimal(value) + "s";
  } else if (
    field.includes("pm") ||
    field === "PM2_5" ||
    field === "mass_conc_pm1_0" ||
    field === "mass_conc_pm2_5" ||
    field === "mass_conc_pm10"
  ) {
    return Array.isArray(value)
      ? formatDecimal(value[0]) + "µg/m³"
      : formatDecimal(value) + "µg/m³";
  } else if (field === "gas_pressure_psi") {
    return formatDecimal(value) + "psi";
  } else if (field === "idc_intput_ma") {
    return formatDecimal(value) + "mA";
  } else if (
    field === "door_open" ||
    field === "door_open2" ||
    field === "water_leak" ||
    field === "state"
  ) {
    return value === 1 || value === true ? "Yes" : "No";
  } else if (field === "sos_event") {
    return value === 1 || value === true ? "Yes" : "No";
  } else if (field === "motion") {
    return value ? "Yes" : "No";
  } else if (field === "leak") {
    return value === 1 || value === true ? "Yes" : "No";
  } else if (field === "isLost") {
    return value ? "Yes" : "No";
  } else if (field === "ignition") {
    return value ? "On" : "Off";
  } else if (field === "plug_state") {
    return value ? "On" : "Off";
  } else if (field === "alarm") {
    return value ? "Yes" : "No";
  } else if (field === "leak_status") {
    return value === "NoLeak" ? "No Leak" : value;
  } else if (field.startsWith("B") && !isNaN(field.substring(1))) {
    return value === 1 ? "Pressed" : "Not Pressed";
  } else if (field === "AQI" || field === "CO2" || field === "TVOC") {
    return Array.isArray(value)
      ? formatDecimal(value[0])
      : formatDecimal(value);
  } else if (field === "package_opened") {
    return value ? "Opened" : "Closed";
  }

  // Apply decimal separator to any remaining numeric values
  if (typeof value === "number") {
    return formatDecimal(value);
  }

  return value;
};

const DeviceCard = (props) => {
  const { device } = props;
  const deviceFrames = useMemo(
    //json decode from device.last_data
    () => getDeviceLastData(device),
    [device]
  );

  const { t } = useTranslation("common");
  const freshnessTime = getDeviceFreshnessTime(device);

  const theme = useTheme();

  const navigate = useNavigate();

  const handleNavigateToDevice = useCallback(() => {
    navigate(`/device/${device.id}`);
  }, [navigate, device.id]);

  const deviceTypeConfig = useMemo(() => {
    return DEVICE_TYPE_LAYOUT[device.device_type]
      ? DEVICE_TYPE_LAYOUT[device.device_type][0]
      : null;
  }, [device.device_type]);

  const { deviceStatus, isActive, batteryPercent, batteryColor } =
    useMemo(() => {
      // Use battery_pct if available (already a percentage)
      let calculatedBatteryPercent = null;
      if (
        deviceFrames?.battery_pct !== undefined &&
        deviceFrames?.battery_pct !== null
      ) {
        calculatedBatteryPercent = deviceFrames.battery_pct + "%";
      } else {
        // Fall back to millivolt conversion
        const calculatedVoltageMv = getBatteryMillivolts(
          deviceFrames,
          device,
          0
        );
        calculatedBatteryPercent = calculatedVoltageMv
          ? convertMvToPercent(calculatedVoltageMv, device.device_brand)
          : null;
      }

      return {
        deviceStatus: deviceFrames?.battery_status ?? "N/A",
        isActive: isDeviceActive(freshnessTime),
        batteryPercent: calculatedBatteryPercent,
        batteryColor: calculatedBatteryPercent
          ? convertPercentToColour(calculatedBatteryPercent)
          : theme.palette.text.secondary,
      };
    }, [deviceFrames, device, freshnessTime, theme]);

  const lastSeenFormatted = useMemo(() => {
    return freshnessTime ? formatDate(freshnessTime) : null;
  }, [freshnessTime]);

  return (
    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
      <>
        <Card
          elevation={0}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderRadius: "12px",
            boxShadow: "0 4px 20px 0 rgba(0,0,0,0.1)",
            transition:
              "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
            "&:hover": {
              boxShadow: "0 10px 25px 0 rgba(0,0,0,0.2)",
            },
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            cursor: "pointer",
          }}
          onClick={handleNavigateToDevice}
        >
          <CardHeader
            action={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                {/* Battery Display */}
                {deviceTypeConfig && deviceTypeConfig.showBattery ? (
                  <Tooltip
                    title={
                      batteryPercent
                        ? `${t("common.battery")}: ${batteryPercent}`
                        : `${t("common.battery")}: N/A`
                    }
                    position="top"
                    arrow
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        backgroundColor: alpha(
                          theme.palette.background.paper,
                          0.8
                        ),
                        border: `1px solid ${alpha(
                          theme.palette.divider,
                          0.2
                        )}`,
                        borderRadius: "8px",
                        px: 1,
                        py: 0.5,
                        minWidth: "60px",
                        justifyContent: "center",
                      }}
                    >
                      <BatteryFull
                        sx={{
                          color: batteryColor,
                          fontSize: "0.75rem",
                        }}
                      />
                      <Typography
                        sx={{
                          color: batteryColor,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                        variant="body2"
                      >
                        {batteryPercent ?? "N/A"}
                      </Typography>
                    </Box>
                  </Tooltip>
                ) : device.device_type === 28 ? (
                  <Tooltip
                    title={
                      deviceStatus ? "Battery: " + deviceStatus : "Battery: N/A"
                    }
                    position="top"
                    arrow
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        backgroundColor: alpha(
                          theme.palette.background.paper,
                          0.8
                        ),
                        border: `1px solid ${alpha(
                          theme.palette.divider,
                          0.2
                        )}`,
                        borderRadius: "8px",
                        px: 1,
                        py: 0.5,
                        minWidth: "60px",
                        justifyContent: "center",
                      }}
                    >
                      <BatteryFull
                        sx={{
                          color: deviceStatus === "OK" ? "green" : "red",
                          fontSize: "1rem",
                        }}
                      />
                      <Typography
                        sx={{
                          color: deviceStatus === "OK" ? "green" : "red",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                        variant="body2"
                      >
                        {deviceStatus === "OK"
                          ? "OK"
                          : deviceStatus === "LOW_BATTERY"
                            ? "Low"
                            : "N/A"}
                      </Typography>
                    </Box>
                  </Tooltip>
                ) : (
                  //show an offline, online icon based on device status
                  <Tooltip
                    title={
                      isActive
                        ? t("common.device_online")
                        : t("common.device_offline")
                    }
                    position="top"
                    arrow
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        backgroundColor: alpha(
                          theme.palette.background.paper,
                          0.8
                        ),
                        border: `1px solid ${alpha(
                          theme.palette.divider,
                          0.2
                        )}`,
                        borderRadius: "8px",
                        px: 1,
                        py: 0.5,
                        minWidth: "60px",
                        justifyContent: "center",
                      }}
                    >
                      <DeviceStatusIcon
                        status={isActive}
                        sx={{ fontSize: "1rem" }}
                      />
                      <Typography
                        sx={{
                          color: isActive ? "green" : "red",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                        variant="body2"
                      >
                        {isActive ? "Online" : "Offline"}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}

                {/* Last Seen Display */}
                <Tooltip
                  title={
                    freshnessTime
                      ? `${t("common.last_seen")}: ${lastSeenFormatted}`
                      : "No data received yet"
                  }
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      backgroundColor: alpha(
                        theme.palette.background.paper,
                        0.8
                      ),
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      borderRadius: "8px",
                      px: 1,
                      py: 0.5,
                      mt: 0.5,
                      minWidth: "60px",
                      justifyContent: "center",
                    }}
                  >
                    <AccessTime
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "1rem",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      }}
                    >
                      {lastSeenFormatted ?? "No data"}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            }
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: "20px",
                    width: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: theme.palette.primary.main,
                  }}
                >
                  <DeviceTypeAvatar device={device} />
                </Box>
                <Box>
                  <Typography fontWeight={600} variant="body1">
                    {device.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "0.75rem",
                      fontFamily: "monospace",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {device.dev_eui}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    {device.tags?.length > 0 ? (
                      device.tags.map((tag) => (
                        <Chip
                          key={tag.id}
                          label={tag.name}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            borderRadius: "16px",
                            backgroundColor: tag.color
                              ? alpha(tag.color, 0.15)
                              : alpha(theme.palette.primary.main, 0.15),
                            color: tag.color || theme.palette.primary.main,
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      ))
                    ) : (
                      <Chip
                        label={t("labels.no_tags")}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          borderRadius: "16px",
                          backgroundColor: alpha(theme.palette.grey[500], 0.1),
                          color: alpha(theme.palette.text.secondary, 0.5),
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            }
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              p: 1,
              pl: 1.4,
            }}
          />
          <CardContent sx={{ flexGrow: 1, p: 3 }}>
            <Grid container spacing={1}>
              {deviceTypeConfig && deviceTypeConfig.fields ? (
                // Dynamically render fields based on device type configuration
                deviceTypeConfig.fields.map((field) => (
                  <Grid key={field.name} size={{ xs: field.width }}>
                    <DeviceCardPanel
                      title={t(
                        "common." +
                          field.name.replace(/\s+/g, "_").toLowerCase()
                      )}
                      content={
                        deviceFrames && deviceFrames[field.field] !== undefined
                          ? getFormattedContent(
                              field.field,
                              deviceFrames[field.field]
                            )
                          : "N/A"
                      }
                    />
                  </Grid>
                ))
              ) : (
                // Fallback for device types not in configuration
                <Grid size={{ xs: 12 }}>
                  <DeviceCardPanel
                    title={t("common.data")}
                    content={
                      deviceFrames
                        ? t(
                            "common.device_data_available_no_display_configured"
                          )
                        : "No data available"
                    }
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </>
    </Grid>
  );
};

// Export with React.memo to prevent unnecessary re-renders
export default React.memo(DeviceCard);
